/**
 * HOST_CHROME — Mission stage IA (Guide pipeline / landing).
 *
 * Project primary chrome is the UnderlineNav (Files · Issues · Changes · Agents · Settings).
 * See `underline-nav.ts`. These stages are the Guide next-up pipeline and marketing copy,
 * not a full-height left rail.
 *
 * Stages (fixed): Guide · Setup · Spec · Design · Verify · Review · Track.
 *
 * Stage → route (existing pages under `/o/:org/p/:project/...` — no stub destinations):
 *   Guide  → project home (redirects to Files)
 *   Setup  → settings (+ hosting / import, mcp)
 *   Spec   → requirements
 *   Design → hardware (redirects to Files) (+ mind → Agents)
 *   Verify → jobs (import / circuit_diff / checks)
 *   Review → reviews (redirects to Changes)
 *   Track  → issues (+ milestones, releases)
 *
 * Brand / PCBX mark → org overview (`/o/:org`) via missionBrandHref.
 *
 * Verify + Review are the checks/reviews family. Track is issues / milestones / releases.
 * Scheme / Sim / Firmware mentioned in older marketing docs stay off this rail.
 */

export type MissionStageId = "guide" | "setup" | "spec" | "design" | "verify" | "review" | "track";

/** Checklist / next-up pipeline (Guide is the board itself, not a pipeline row). */
export type WorkflowStageId = Exclude<MissionStageId, "guide">;

export type NestedStageLink = { label: string; to: string };

export type WorkflowStage = {
  id: MissionStageId;
  num: string;
  title: string;
  desc: string;
  /** Path under the project base. Empty string = project home (Guide). */
  to: string;
  nested?: NestedStageLink[];
};

/** Product lock: v1 sidebar labels, in order. */
export const HOST_CHROME_STAGE_TITLES = [
  "Guide",
  "Setup",
  "Spec",
  "Design",
  "Verify",
  "Review",
  "Track",
] as const;

export const MISSION_STAGES: WorkflowStage[] = [
  {
    id: "guide",
    num: "",
    title: "Guide",
    desc: "Mission guide · next steps",
    to: "",
  },
  {
    id: "setup",
    num: "01",
    title: "Setup",
    desc: "Project · hosting · settings",
    to: "settings",
    nested: [
      { label: "Hosting", to: "settings/git" },
      { label: "MCP", to: "settings/mcp" },
    ],
  },
  {
    id: "spec",
    num: "02",
    title: "Spec",
    desc: "Requirements",
    to: "requirements",
  },
  {
    id: "design",
    num: "03",
    title: "Design",
    desc: "Upload KiCad zip · SoT",
    to: "hardware",
    nested: [{ label: "Mind", to: "mind" }],
  },
  {
    id: "verify",
    num: "04",
    title: "Verify",
    desc: "import / circuit_diff / checks",
    to: "jobs",
  },
  {
    id: "review",
    num: "05",
    title: "Review",
    desc: "Design Reviews (main stage)",
    to: "reviews",
  },
  {
    id: "track",
    num: "06",
    title: "Track",
    desc: "Issues · milestones · releases",
    to: "issues",
    nested: [
      { label: "Milestones", to: "milestones" },
      { label: "Releases", to: "releases" },
    ],
  },
];

export type PipelineStage = WorkflowStage & { id: WorkflowStageId };

/** Stages 01–06 for the Guide checklist (Guide is the page, not a row). */
export const WORKFLOW_STAGES: PipelineStage[] = MISSION_STAGES.filter(
  (s): s is PipelineStage => s.id !== "guide",
);

export function missionStageHref(projectBase: string, to: string): string {
  const root = projectBase.replace(/\/+$/, "");
  if (!to) return root || "/";
  return `${root}/${to}`;
}

/**
 * Topbar PCBX / RS mark. Always org overview when an org is in scope.
 * Never project Guide (`/o/:org/p/:project`). Outside org scope, `/` (landing).
 */
export function missionBrandHref(orgBase: string | null | undefined): string {
  const href = (orgBase ?? "").replace(/\/+$/, "");
  if (!href) return "/";
  const orgMatch = href.match(/^(\/o\/[^/]+)/);
  return orgMatch ? orgMatch[1] : "/";
}

/** Path after `/o/:org/p/:project/` or null when not in a project. */
export function pathAfterProject(pathname: string): string | null {
  const match = pathname.match(/\/p\/[^/]+\/?(.*)$/);
  if (!match) return null;
  return match[1] ?? "";
}

export type ShellHighlight = MissionStageId;

export function restMatches(rest: string, to: string): boolean {
  if (!to) return rest === "";
  return rest === to || rest.startsWith(`${to}/`);
}

export function shellHighlight(pathname: string): ShellHighlight | null {
  const rest = pathAfterProject(pathname);
  if (rest === null) return null;
  if (rest === "") return "guide";
  if (rest === "settings" || rest.startsWith("settings/") || rest === "help/mcp" || rest.startsWith("help/mcp")) {
    return "setup";
  }
  if (rest === "requirements" || rest.startsWith("requirements/")) return "spec";
  if (rest === "hardware" || rest.startsWith("hardware/") || rest === "mind" || rest.startsWith("mind/")) {
    return "design";
  }
  if (rest === "jobs" || rest.startsWith("jobs/")) return "verify";
  if (rest === "reviews" || rest.startsWith("reviews/")) return "review";
  if (
    rest === "issues" ||
    rest.startsWith("issues/") ||
    rest === "milestones" ||
    rest.startsWith("milestones/") ||
    rest === "releases" ||
    rest.startsWith("releases/")
  ) {
    return "track";
  }
  return null;
}

export function isNestedOn(pathname: string, to: string): boolean {
  const rest = pathAfterProject(pathname);
  if (rest === null) return false;
  return restMatches(rest, to);
}

/** Track nested: `/milestones` vs `/releases` must select the matching pane. */
export function trackPane(pathname: string): "ms" | "rel" | null {
  const rest = pathAfterProject(pathname);
  if (rest === null) return null;
  if (rest === "releases" || rest.startsWith("releases/")) return "rel";
  if (rest === "milestones" || rest.startsWith("milestones/")) return "ms";
  return null;
}

/** ⌘K MISSION jumps require `/o/:org/p/:project`, not org overview. */
export function isProjectMissionBase(base: string | null | undefined): base is string {
  if (!base) return false;
  return /\/o\/[^/]+\/p\/[^/]+/.test(base);
}
